import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../config';

const PAGE_STATE_KEY = '__portfolioVisitorCounterPageState';

const createVisitorId = () => {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const getPageState = () => {
  if (!window[PAGE_STATE_KEY]) {
    window[PAGE_STATE_KEY] = {
      visitorId: createVisitorId(),
      shown: false,
      registrationStarted: false
    };
  }

  return window[PAGE_STATE_KEY];
};

const VisitorCounter = () => {
  const pageState = getPageState();
  const [count, setCount] = useState(0);
  const [show, setShow] = useState(false);
  const [pulse, setPulse] = useState(false);
  const countRef = useRef(count);

  useEffect(() => {
    countRef.current = count;
  }, [count]);

  useEffect(() => {
    const handleScroll = () => {
      const documentHeight = Math.max(
        document.body.offsetHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );
      const bottom = documentHeight - (window.scrollY + window.innerHeight);

      if (pageState.shown) {
        if (bottom >= 500) {
          setShow(false);
        }
        return;
      }

      if (bottom < 500) {
        pageState.shown = true;
        setShow(true);
        setTimeout(() => {
          setShow(false);
        }, 5000);
      } else {
        setShow(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [pageState]);

  const getBases = () => {
    const bases = [];
    if (API_BASE) {
      bases.push(API_BASE);
    } else if (window.location.hostname !== 'localhost' && window.location.protocol !== 'file:') {
      bases.push(window.location.origin);
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[VisitorCounter] REACT_APP_API_URL is not set. Using same-origin fallback. Set REACT_APP_API_URL on Render to your backend URL if the API is on a different domain.');
      }
    }
    return bases;
  };

  useEffect(() => {
    if (!show || pageState.registrationStarted) {
      return;
    }

    pageState.registrationStarted = true;
    const visitorId = pageState.visitorId;

    const registerVisit = async () => {
      for (const base of getBases()) {
        try {
          const res = await fetch(`${base}/api/visitors`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ visitorId })
          });

          if (!res.ok) continue;
          const contentType = res.headers.get('content-type') || '';
          if (!contentType.includes('application/json')) continue;

          const data = await res.json();
          if (typeof data.count === 'number') {
            setCount(data.count);
            return;
          }
        } catch (err) {
          console.error(`[VisitorCounter] Failed to register visit at ${base}/api/visitors:`, err.message);
        }
      }
    };

    registerVisit();
  }, [show, pageState]);

  useEffect(() => {
    const fetchCount = async () => {
      for (const base of getBases()) {
        try {
          const res = await fetch(`${base}/api/visitors`);
          if (!res.ok) continue;
          const contentType = res.headers.get('content-type') || '';
          if (!contentType.includes('application/json')) continue;
          const data = await res.json();
          if (typeof data.count === 'number' && data.count !== countRef.current) {
            setCount(data.count);
            setPulse(true);
            setTimeout(() => setPulse(false), 600);
            return;
          }
        } catch (err) {
          console.error(`[VisitorCounter] Failed to fetch count from ${base}/api/visitors:`, err.message);
        }
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className={`visitor-counter ${show ? 'show' : ''} ${pulse ? 'pulse' : ''}`} data-tour="visitor-counter">
      <span>{count.toLocaleString()} visited</span>
    </div>
  );
};

export default VisitorCounter;
